import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import ctaBg from '../landing/assets/about_us_4.webp';
import iconApple from '../landing/assets/Apple.png';
import iconGoogle from '../landing/assets/Playstore.png';
import squashTournamentHeroBg from '../landing/assets/squash-tournament/hero2.webp';
import squashTournamentFeaturesBg from '../landing/assets/squash-tournament/Squash2.webp';
import squashTournamentWhyBg from '../landing/assets/squash-tournament/Squash4.webp';
import squashTournamentIcon3 from '../landing/assets/svgs/dynamic_icon.svg';
import squashTournamentIcon4 from '../landing/assets/svgs/player_team_icon.svg';
import squashTournamentIcon1 from '../landing/assets/svgs/scheduling_icon.svg';
import squashTournamentIcon2 from '../landing/assets/svgs/scoring_icon.svg';
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

const FAQs = [
    {
        q: "How do I start using ScorePal for a squash tournament?",
        a: <>During setup, ScorePal collects and processes personal details such as player names and match information only to run the tournament smoothly. It only takes a few minutes to set everything up, even if you’re organizing an event for the first time.</>,
    },
    {
        q: "Does ScorePal support live scoring for squash matches?",
        a: <>Yes, you can update points in real time, and the brackets and standings refresh instantly for everyone. This helps players and spectators control the action without waiting for manual updates.</>,
    },
    {
        q: "Can I run an online squash tournament with ScorePal?",
        a: <>Absolutely. Players can register online, check schedules, and follow match results from any device, while the processing of personal data is limited to tournament registration, scheduling, and result updates. It makes hosting weekend events or multi-day tournaments much easier for organizers.</>,
    },
    {
        q: "Does the software help with player rankings or stats?",
        a: <>Yes, ScorePal tracks match results and simple performance stats, giving organizers helpful sports analytics during the tournament. These insights make it easier to seed players fairly and monitor overall progress.</>,
    },
    {
        q: "Is ScorePal easy to use for clubs and schools?",
        a: <>Yes. It’s designed for all levels—coaches, volunteers, and event staff can run tournaments without any technical skills. Most users can find and manage their entire event or game with just a few clicks. All schedules and results can be shared instantly with players so everyone stays updated throughout the event.</>,
    },
    {
        q: "Can ScorePal manage different draw formats?",
        a: <>Yes, it supports single elimination, double elimination, round robin, and custom formats, making it flexible for any squash event. This gives you the freedom to design a draw that suits your players and available courts.</>,
    },
];

export const Route = createFileRoute('/squash-tournament-software')({
    component: RouteComponent,
})

function RouteComponent() {
    const features = [
        {
            icon:        squashTournamentIcon1,
            title:       'Effortless Match Scheduling',
            description: 'Build accurate, conflict-free schedules in minutes. ScorePal automatically considers court availability, match duration, and player participation, ensuring your tournament stays on track.',
        },
        {
            icon:        squashTournamentIcon2,
            title:       'Real-Time Scoring & Live Updates',
            description: 'Record scores quickly and easily with ScorePal’s intuitive scoring interface. Draws, standings, and brackets update instantly so players and spectators always know what’s happening.',
        },
        {
            icon:        squashTournamentIcon3,
            title:       'Professional Draw Creation',
            description: 'Generate organized, balanced tournament draws for single-elimination, double-elimination, round robin, or custom formats. ScorePal, squash management software, keeps brackets clean and easy to follow from start to finish.',
        },
        {
            icon:        squashTournamentIcon4,
            title:       'Streamlined Player Management',
            description: 'Manage registrations, seedings, divisions, and player profiles all in one centralized system. ScorePal, squash scheduling software, helps reduce admin workload and keeps your event data accurate.',
        },
    ];

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context":   "https://schema.org",
                "@type":      "FAQPage",
                "mainEntity": [
                    {
                        "@type":          "Question",
                        "name":           "How do I start using ScorePal for a squash tournament?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "During setup, ScorePal collects and processes personal details such as player names and match information only to run the tournament smoothly. It only takes a few minutes to set everything up, even if you’re organizing an event for the first time."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Does ScorePal support live scoring for squash matches?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, you can update points in real time, and the brackets and standings refresh instantly for everyone. This helps players and spectators control the action without waiting for manual updates."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can I run an online squash tournament with ScorePal?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Absolutely. Players can register online, check schedules, and follow match results from any device, while the processing of personal data is limited to tournament registration, scheduling, and result updates. It makes hosting weekend events or multi-day tournaments much easier for organizers."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Does the software help with player rankings or stats?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, ScorePal tracks match results and simple performance stats, giving organizers helpful sports analytics during the tournament. These insights make it easier to seed players fairly and monitor overall progress."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Is ScorePal easy to use for clubs and schools?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. It’s designed for all levels—coaches, volunteers, and event staff can run tournaments without any technical skills. Most users can find and manage their entire event or game with just a few clicks. All schedules and results can be shared instantly with players so everyone stays updated throughout the event."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can ScorePal manage different draw formats?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, it supports single elimination, double elimination, round robin, and custom formats, making it flexible for any squash event. This gives you the freedom to design a draw that suits your players and available courts."
                        }
                    }
                ]
            }} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context":            "https://schema.org",
                "@type":               "SoftwareApplication",
                "name":                "Squash Tournament Software",
                "url":                 "https://scorepal.co/squash-tournament-software/",
                "applicationCategory": "SportsApplication",
                "operatingSystem":     ["Web", "Android", "iOS"],
                "description":         "Run professional squash tournaments with automated fixtures, player management, and live scoring tools built for clubs and leagues using ScorePal. Proven performance over the past 2 years.",
                "publisher":           {
                    "@type": "Organization",
                    "name":  "ScorePal",
                    "url":   "https://scorepal.co/"
                }
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Squash Tournament Software", item: "https://scorepal.co/squash-tournament-software/"}]} />
            <title>Squash Tournament Software for Clubs & Organizers | Smart Scheduling</title>
            <meta name="description" content="Manage squash tournaments with accurate scoring, smart scheduling, and clear match tracking. Built for clubs to run events effortlessly." />
            <link rel="canonical" href={`${config.publicUrl}/squash-tournament-software/`} />

            <section className="relative overflow-hidden p-4">
                <div
                    className="flex flex-col min-h-screen justify-between relative pb-16 rounded-4xl bg-[#EDEEF4] bg-center bg-cover bg-no-repeat saturate-[0.87]"
                    style={{
                        backgroundImage: `url(${squashTournamentHeroBg})`
                    }}
                >
                    <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />

                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
                        <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
                            Squash Tournament Software
                        </h1>

                        <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
                            Squash tournament software gives clubs clearer match management with accurate scoring, structured play, and straightforward tournament organization.
                        </p>

                        <Link
                            to={"/signup/player/" as any}
                            className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>

                        <div className="flex flex-wrap gap-4 mt-16 lg:mt-60">
                            <a
                                href="https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-100 transition">
                                    <img src={iconGoogle} alt="Google Play" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">GET IT ON</div>
                                        <div className="text-white font-semibold text-sm">Google Play</div>
                                    </div>
                                </div>
                            </a>

                            <a
                                href="https://apps.apple.com/us/app/score-pal/id6745509167"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={iconApple} alt="App Store" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">Download on the</div>
                                        <div className="text-white font-semibold text-sm">App Store</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                All-in-One Tools to Streamline<br />Every Squash Event
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                Run smooth, professional squash tournaments with ScorePal’s all-in-one squash tournament software. From scheduling matches and tracking scores to creating draws and managing players, ScorePal provides everything organizers need to deliver efficient and engaging events.
                            </p>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={squashTournamentFeaturesBg}
                                alt="Sports analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
                        {features.map((feature, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-[#EDEEF4] flex items-center justify-center shrink-0">
                                        <img src={feature.icon} alt="" className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-inter text-[#082F8A] font-semibold text-base">
                                        {feature.title}
                                    </h3>
                                </div>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="flex justify-center">
                <Link
                    to={"/signup/player/" as any}
                    className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                >
                    <span className="font-inter text-base font-medium">Lets Get Started</span>
                    <div className="bg-[#082F8A] rounded-full p-1">
                        <ArrowUpRightIcon className="text-white" size={28} />
                    </div>
                </Link>
            </div>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                                Why Organizers Prefer ScorePal’s Squash Tournament Software
                            </h2>

                            <ul className="space-y-4">
                                {[
                                    'Automated scheduling and real-time scoring',
                                    'Flexible draw and format options',
                                    'Ideal for clubs, leagues, and school events',
                                    'Mobile-friendly and simple for staff and volunteers',
                                    'Tournament features are designed to save time and enhance event professionalism',
                                ].map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <CheckBadgeIcon size={24} />
                                        <span className="font-inter text-[#1a1a2e] text-base">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={squashTournamentWhyBg}
                                alt="Why choose ScorePal Analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>

                        <div className="block lg:hidden">
                            <img
                                src={squashTournamentWhyBg}
                                alt="Why choose ScorePal Analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8 lg:py-16">
                <div className="container mx-auto px-6 lg:px-12">
                    <div
                        className="rounded-3xl py-16 lg:py-24 px-8 text-center bg-top bg-cover bg-no-repeat"
                        style={{backgroundImage: `url(${ctaBg})`}}
                    >
                        <h2 className="font-bebas text-white uppercase text-[clamp(28px,4vw,48px)] leading-tight font-bold tracking-wide mb-4">
                            Make Your Squash Tournaments Seamless
                        </h2>
                        <p className="font-inter text-white/90 text-base lg:text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
                            With ScorePal, you can focus on delivering a great event experience while the platform handles the logistics. From setup to final results, ScorePal simplifies every step and works smoothly alongside existing <a href="https://scorepal.co/sports-league-management-software/" className={"not-hover:underline"}>sports league software</a>. Works seamlessly as <a href="https://scorepal.co/create-brackets/" className={"not-hover:underline"}>tournament league software</a> for clubs and community events.
                            <br /><br />
                            Streamline your squash tournaments with ScorePal <strong>squash tournament software</strong> today.
                        </p>
                        <Link
                            to={"/signup/player/" as any}
                            className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>

                    <FaqComponent title={"Faqs About Squash Tournament Software"} faqs={FAQs} />
                </div>
            </section>

            <FooterSection />
        </div>
    );
}

